import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Selectors shared by every posting form, plus the mirrored-master link.
 */
import { Autocomplete, Box, Link, MenuItem, TextField, Typography, } from '@mui/material';
import { useMemo, useState } from 'react';
import { useItems, useWarehouses, useZones, useBins, useHostLinks } from '@inai/inventory-core/react';
export function WarehouseSelect({ value, onChange, label = 'Warehouse', required, helperText, disabled, size = 'small', }) {
    const { data: warehouses = [], isLoading } = useWarehouses();
    return (_jsx(TextField, { select: true, size: size, fullWidth: true, label: label, required: required, disabled: disabled || isLoading, value: value ?? '', helperText: helperText, onChange: (e) => {
            const id = e.target.value || null;
            onChange(id, warehouses.find((w) => w.id === id) ?? null);
        }, children: warehouses.map((w) => (_jsx(MenuItem, { value: w.id, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }, children: [_jsx("span", { children: w.name }), _jsxs(Typography, { component: "span", sx: { fontSize: 12 }, color: "text.secondary", children: [w.code, w.allow_negative_stock ? ' · negative allowed' : ''] })] }) }, w.id))) }));
}
export function ItemSelect({ value, onChange, label = 'Item', required, disabled, stockTrackedOnly = true, helperText, }) {
    const [search, setSearch] = useState('');
    const filter = useMemo(() => ({
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(stockTrackedOnly ? { is_stock_tracked: true } : {}),
        status: 'ACTIVE',
        limit: 50,
    }), [search, stockTrackedOnly]);
    const { data: items = [], isFetching } = useItems(filter);
    return (_jsx(Autocomplete, { size: "small", fullWidth: true, disabled: disabled, options: items, value: value, loading: isFetching, 
        // The server does the searching. Filtering the 50 rows it returned again locally
        // would hide everything past the page boundary.
        filterOptions: (x) => x, isOptionEqualToValue: (a, b) => a.id === b.id, getOptionLabel: (o) => `${o.name} (${o.item_code})`, onInputChange: (_e, text, reason) => {
            if (reason === 'input')
                setSearch(text);
        }, onChange: (_e, next) => onChange(next), renderOption: (props, option) => {
            const { key, ...rest } = props;
            return (_jsx("li", { ...rest, children: _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 600 }, noWrap: true, children: option.name }), _jsxs(Typography, { sx: { fontSize: 12 }, color: "text.secondary", noWrap: true, children: [option.item_code, option.tracking_policy !== 'NONE' ? ` · ${option.tracking_policy}` : ''] })] }) }, key ?? option.id));
        }, renderInput: (params) => (_jsx(TextField, { ...params, label: label, required: required, helperText: helperText })) }));
}
const STOCK_STATES = [
    { value: 'AVAILABLE', label: 'Available', hint: 'On the shelf and pickable' },
    { value: 'IN_TRANSIT', label: 'In transit', hint: 'Dispatched, held against the destination' },
    { value: 'QUARANTINE', label: 'Quarantine', hint: 'Received but not pickable' },
];
export function StockStateSelect({ value, onChange, label = 'Stock state', disabled, }) {
    return (_jsx(TextField, { select: true, size: "small", fullWidth: true, label: label, value: value, disabled: disabled, onChange: (e) => onChange(e.target.value), children: STOCK_STATES.map((s) => (_jsx(MenuItem, { value: s.value, children: _jsxs(Box, { children: [_jsx("span", { children: s.label }), _jsx(Typography, { component: "span", sx: { fontSize: 12, ml: 1 }, color: "text.secondary", children: s.hint })] }) }, s.value))) }));
}
/**
 * Zone then bin. Both are gated on `multi_warehouse`, so this renders nothing useful on
 * the essential tier — which is correct: an essential tenant has one warehouse and no
 * subdivisions, and a bin field would be a dead control.
 */
export function BinSelect({ warehouseId, value, onChange, disabled }) {
    const [zoneId, setZoneId] = useState(null);
    const { data: zones = [] } = useZones(warehouseId);
    const { data: bins = [] } = useBins(zoneId);
    if (zones.length === 0)
        return null;
    return (_jsxs(Box, { sx: { display: 'flex', gap: 1.5 }, children: [_jsxs(TextField, { select: true, size: "small", fullWidth: true, label: "Zone", value: zoneId ?? '', disabled: disabled, onChange: (e) => {
                    setZoneId(e.target.value || null);
                    onChange(null);
                }, children: [_jsx(MenuItem, { value: "", children: _jsx("em", { children: "None" }) }), zones.map((z) => (_jsxs(MenuItem, { value: z.id, children: [z.name, " (", z.zone_type, ")"] }, z.id)))] }), _jsxs(TextField, { select: true, size: "small", fullWidth: true, label: "Bin", value: value ?? '', disabled: disabled || !zoneId, onChange: (e) => onChange(e.target.value || null), children: [_jsx(MenuItem, { value: "", children: _jsx("em", { children: "None" }) }), bins.map((b) => (_jsxs(MenuItem, { value: b.id, disabled: !b.is_pickable, children: [b.name, " (", b.code, ")"] }, b.id)))] })] }));
}
/**
 * A record this module mirrors but does not own.
 *
 * Invariant 6: employees belong to Sapiens, customers to CRM. The module renders a deep
 * link and never an edit form. When the host supplies no link builder the label is plain
 * text — a dead link would be worse than none, and the absence of an edit affordance is
 * the point either way.
 */
export function MirroredMasterLink({ kind, id, label }) {
    const links = useHostLinks();
    const href = id ? (links[kind]?.(id) ?? null) : null;
    const text = label ?? id ?? '—';
    if (!href) {
        return (_jsx(Typography, { component: "span", sx: { fontSize: 13 }, children: text }));
    }
    return (_jsx(Link, { href: href, target: "_blank", rel: "noreferrer", sx: { fontSize: 13 }, children: text }));
}
//# sourceMappingURL=Pickers.js.map